import React, { FC } from "react";

import { BaseComponentProps } from "@keepeek/commons";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import sanitizeHtml from "sanitize-html";

import { CONFIGURATION_SECTION_PATH } from "../../../../components/layout/adminMenu";
import {
  KHeaderWidget,
  KHeaderWidgetLogoPosition,
} from "../../../../models/configuration/components/kHeaderWidget";
import { Key, Theme } from "../../../../models/configuration/theme";
import { useConfiguration } from "../../../../providers/config/hooks";
import { useImageOptim } from "../../../../providers/config/hooks/useImageOptim";
import { configSectionThemeAssetSelector } from "../../../../providers/config/selectors";
import ImageOptim from "../../../ImageOptim";
import { classes } from "../styles";

type KHeaderWidgetLogoProps = {
  kHeaderWidgetLogo: KHeaderWidget["kHeaderWidgetLogo"];
} & BaseComponentProps;

const KHeaderWidgetLogo: FC<React.PropsWithChildren<KHeaderWidgetLogoProps>> = function ({
  kHeaderWidgetLogo,
  ...rest
}) {
  const { locale } = useRouter();
  const { t } = useTranslation();
  const logoAlt =
    kHeaderWidgetLogo?.alt?.find((a) => a.language === locale)?.value || t("app.title") || "";

  const configTheme = useConfiguration<Theme>(CONFIGURATION_SECTION_PATH.THEME);
  const [logoMain] = useRecoilValue<string[]>(configSectionThemeAssetSelector(Key.ImageLogoMain));

  let logoImage: string | undefined = configTheme?.logoMain?.url;
  if (!logoImage) {
    logoImage = logoMain;
  }
  const logoByLocale = kHeaderWidgetLogo?.url?.find((u) => u.language === locale);
  logoImage = logoByLocale?.resource?.url;
  if (!logoImage) {
    logoImage = logoByLocale?.value;
  }

  const { loading, shouldOptimize } = useImageOptim();

  const logoRender = () => {
    if (loading || !logoImage) {
      return null;
    }

    const logoFallback = () => {
      return (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          onContextMenu={(e) => e.preventDefault()}
          aria-hidden
          src={logoImage}
          className={classes.logo}
          alt={sanitizeHtml(logoAlt)}
          style={{
            paddingTop: kHeaderWidgetLogo?.kHeaderWidgetLogoSpacing?.top,
            paddingRight: kHeaderWidgetLogo?.kHeaderWidgetLogoSpacing?.right,
            paddingLeft: kHeaderWidgetLogo?.kHeaderWidgetLogoSpacing?.top,
            paddingBottom: kHeaderWidgetLogo?.kHeaderWidgetLogoSpacing?.right,
            marginRight:
              kHeaderWidgetLogo?.kHeaderWidgetLogoPosition === KHeaderWidgetLogoPosition.Right
                ? "1rem"
                : 0,
            marginLeft:
              kHeaderWidgetLogo?.kHeaderWidgetLogoPosition === KHeaderWidgetLogoPosition.Right
                ? "1rem"
                : 0,
          }}
        />
      );
    };

    if (shouldOptimize) {
      return (
        <ImageOptim
          src={logoImage}
          className={classes.logo}
          alt={logoAlt}
          style={{
            paddingTop: kHeaderWidgetLogo?.kHeaderWidgetLogoSpacing?.top,
            paddingRight: kHeaderWidgetLogo?.kHeaderWidgetLogoSpacing?.right,
            paddingLeft: kHeaderWidgetLogo?.kHeaderWidgetLogoSpacing?.top,
            paddingBottom: kHeaderWidgetLogo?.kHeaderWidgetLogoSpacing?.right,
            marginRight:
              kHeaderWidgetLogo?.kHeaderWidgetLogoPosition === KHeaderWidgetLogoPosition.Right
                ? "1rem"
                : 0,
            marginLeft:
              kHeaderWidgetLogo?.kHeaderWidgetLogoPosition === KHeaderWidgetLogoPosition.Right
                ? "1rem"
                : 0,
            width: "auto",
          }}
          width={(window.innerHeight / 100) * 8}
          height={(window.innerHeight / 100) * 8}
          priority
          quality={100}
          unoptimized
        >
          {logoFallback()}
        </ImageOptim>
      );
    }
    return logoFallback();
  };

  return (
    <h1 className={classes.logoTitle} {...rest}>
      {logoRender()}
    </h1>
  );
};

export default KHeaderWidgetLogo;
